// DESIGN.md §4.D — Editor / form archetype shared chrome.
import type { ReactElement, ReactNode } from 'react';
import { cloneElement, isValidElement, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslate } from '@tolgee/react';
import {
  MudButton,
  MudDateInput,
  MudIcon,
  MudInfoBox,
  MudInlineMessage,
  MudNumericInput,
  MudPhoneInput,
  MudSearchInput,
  MudSelect,
  MudTextarea,
  MudTextInput,
  MudTimeInput,
  MudTooltip,
} from '@egov-moldova/mud-react';
import { Breadcrumb, DraftDateInput, DraftNumericInput } from '@simsm/ui-shared';
import type { ApiRefusal, LoadFailureKind } from '../../app/api-client';
import { useConfirm } from '../ConfirmDialog/ConfirmDialog';
import { LOAD_ERROR_KEYS } from './load-error-copy';

interface BreadcrumbLink {
  label: string;
  href?: string;
}

interface Props {
  breadcrumb: BreadcrumbLink[];
  title: string;
  lede?: string;
  children: ReactNode;
  saveLabel: string;
  isSaving?: boolean;
  isDirty?: boolean;
  isLoading?: boolean;
  // Why the record could not be shown, from `loadFailureKind`. Three states and
  // not two: "does not exist" is a claim about the record, and a read that was
  // refused is not evidence for it.
  loadError?: LoadFailureKind | null;
  // A refused write, headline and reason kept apart, from `apiRefusal`. The only
  // way in: the single-slot `errorMessage` it replaced hard-coded
  // "form.error.save_failed" as the headline, so a refused delete announced a
  // failed save, and a caller that had already resolved the same sentence into
  // the slot got it printed twice — headline and body, word for word.
  refusal?: ApiRefusal | null;
  // Accepted for backward compat but no longer disables Save: validation now runs
  // in each form's onSave, which reveals inline field errors instead of a dead button.
  canSave?: boolean;
  onSave: () => void;
  onCancel: () => void;
}

export function FormPage({
  breadcrumb,
  title,
  lede,
  children,
  saveLabel,
  isSaving = false,
  isDirty = false,
  isLoading = false,
  loadError = null,
  refusal = null,
  onSave,
  onCancel,
}: Props): JSX.Element {
  const { t } = useTranslate();
  const navigate = useNavigate();
  const confirm = useConfirm();

  // The banner's headline names the operation that failed and its body says why
  // the server refused. When no reason came back the operation sentence is the
  // whole message, so it stands alone as the body — printing it as headline and
  // body both would just say the same thing twice.
  const bannerTitle = refusal && refusal.detailKey ? t(refusal.titleKey) : null;
  const bannerBody = refusal ? t(refusal.detailKey ?? refusal.titleKey) : null;

  useEffect(() => {
    if (!isDirty) return;
    function handler(e: BeforeUnloadEvent): void {
      e.preventDefault();
      e.returnValue = '';
    }
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  // Leaving a dirty form is asked through the shared AGE modal, the same
  // dialog every other destructive confirmation in the product uses; the
  // native window.confirm it replaced was the last browser-drawn control here.
  async function handleCancel(): Promise<void> {
    if (isDirty) {
      const leave = await confirm({
        title: t('form.unsaved.title'),
        body: t('form.unsaved_warning'),
        confirmLabel: t('form.unsaved.discard'),
        cancelLabel: t('form.unsaved.stay'),
        destructive: true,
      });
      if (!leave) return;
    }
    onCancel();
  }

  if (loadError) {
    const backHref = breadcrumb[0]?.href;
    return (
      <section className="simsm-page simsm-form-page simsm-form-page--error">
        <p>{t(LOAD_ERROR_KEYS[loadError])}</p>
        {backHref ? (
          <MudButton
            shape="circular"
            variant="neutral"
            size="md"
            onClick={() => navigate(backHref)}
          >
            {t('form.action.back_to_list')}
          </MudButton>
        ) : null}
      </section>
    );
  }

  return (
    <section className="simsm-page simsm-form-page">
      <Breadcrumb
        maxVisible={5}
        label={t('common.breadcrumb.aria_label')}
        crumbs={breadcrumb.map((crumb) => ({ label: crumb.label, to: crumb.href }))}
      />

      <header className="simsm-form-page__header">
        <h1 className="simsm-form-page__title">{title}</h1>
        {lede ? <p className="simsm-form-page__lede">{lede}</p> : null}
      </header>

      {bannerBody ? (
        // The refusal appears after Save, so it is announced: on AGE the
        // notification carried role="alert" itself, and mud-info-box has none (059
        // V-082).
        <MudInfoBox
          emphasis="strong"
          role="alert"
          variant="warning"
          {...(bannerTitle ? { titleText: bannerTitle } : {})}
        >
          {bannerBody}
        </MudInfoBox>
      ) : null}

      <div className="simsm-form-page__body simsm-page__group" aria-busy={isLoading}>
        {children}
      </div>

      {/* The wireframe's `config-acte-jos`: a rule above, the actions at the
          right, the way back as an outlined button beside the one filled one. */}
      <div className="simsm-form-page__footer simsm-page__group">
        <MudButton
          shape="circular"
          variant="strict"
          appearance="outlined"
          size="md"
          disabled={isSaving}
          onClick={() => void handleCancel()}
        >
          {t('form.action.cancel')}
        </MudButton>
        {/* `loading`, not just `disabled`: AGE's own pending state is a centred
            spinner plus `aria-busy`, and it blocks interactivity by itself. A
            greyed-out button whose label quietly changed to "Se salvează." was
            the whole feedback a save had, and it read as nothing happening —
            which is exactly how a create that took 48s server-side was
            experienced. */}
        <MudButton
          shape="circular"
          variant="primary"
          size="md"
          loading={isSaving}
          disabled={isSaving}
          onClick={onSave}
        >
          {isSaving ? t('form.action.saving') : saveLabel}
        </MudButton>
      </div>
    </section>
  );
}

/**
 * One band of the form: what this group of fields is, on the left; the fields
 * themselves, on the right.
 *
 * The two columns are the wireframe's `.config-sect`, which is the shape all six
 * of its administration editors are drawn in (AdminCoduri, AdminAgregari,
 * AdminLocatii, AdminSolicitari, AdminConfigurari, AdminAlerte) — so this is the
 * pattern, not one page's exception. What it replaced here was a stack: a 12px
 * uppercase label above the fields, which read as a caption rather than a
 * heading and had nowhere to put the sentence saying what the group is for.
 * MUD writes headings in sentence case, so the uppercasing went with it.
 *
 * `lede` is a prop and not a child because that sentence belongs in the left
 * column, beside the title. The three pages that used to pass it as a
 * `<p className="simsm-form-page__section-lede">` child were passing it into a
 * class no stylesheet ever defined.
 */
export function FormSection({
  title,
  lede,
  children,
}: {
  // A node, not a string: the wireframe hangs an info tip off two of these
  // headings, and the explanation it carries has nowhere else on the page to
  // live.
  title?: ReactNode;
  lede?: ReactNode;
  children: ReactNode;
}): JSX.Element {
  return (
    <section className="simsm-form-page__section">
      {title || lede ? (
        <div className="simsm-form-page__section-head">
          {title ? <h2 className="simsm-form-page__section-title">{title}</h2> : null}
          {lede ? <p className="simsm-form-page__section-lede">{lede}</p> : null}
        </div>
      ) : null}
      <div className="simsm-form-page__section-body">{children}</div>
    </section>
  );
}

// The MUD fields that take their accessible name from the host, and so need it
// handed over when a FormField labels them.
const NAMED_FROM_HOST: ReadonlySet<unknown> = new Set<unknown>([
  MudDateInput,
  MudNumericInput,
  MudPhoneInput,
  MudSearchInput,
  MudSelect,
  MudTextarea,
  MudTextInput,
  MudTimeInput,
  DraftDateInput,
  DraftNumericInput,
]);

type FieldProps = { invalid?: boolean; label?: unknown; 'aria-label'?: unknown };

function withFieldProps(
  child: ReactElement,
  label: string,
  error: string | null | undefined,
): ReactElement {
  const props = child.props as FieldProps;
  const extra: FieldProps = {};
  if (error) extra.invalid = true;
  if (
    NAMED_FROM_HOST.has(child.type) &&
    props['aria-label'] === undefined &&
    props.label === undefined
  ) {
    extra['aria-label'] = label;
  }
  return Object.keys(extra).length > 0 ? cloneElement(child, extra) : child;
}

export function FormField({
  label,
  children,
  hint,
  info,
  fullWidth,
  required = false,
  error = null,
}: {
  label: string;
  children: ReactNode;
  hint?: string;
  /** The wireframe's InfoTip beside a label: an explanation too long for a hint. */
  info?: string | undefined;
  fullWidth?: boolean;
  required?: boolean;
  // When set, an inline error replaces the hint and marks the field invalid.
  // `| undefined` so callers can pass `submitted ? msg : undefined` under
  // exactOptionalPropertyTypes.
  error?: string | null | undefined;
}): JSX.Element {
  // The control has to carry the error too, not just the sentence under it.
  // Every AGE control used in these forms takes `invalid`, and it is what draws
  // the destructive border and sets `aria-invalid` — without it the field read
  // as untouched and only a line of red text below said otherwise, which is the
  // one part of the message a screen reader was not going to tie to the input.
  // Injected here rather than at each call site so the border and the message
  // cannot disagree: they are the same `error`.
  //
  // Only ever set to `true`. Passing `invalid={false}` would overrule a control
  // that manages its own invalid state (AggregateFormPage does).
  //
  // The label has to reach the control's accessible name as well. A MUD field
  // draws its input inside its shadow root and names it only from the host's
  // `aria-label` (or its own `label`), so the `<label>` wrapping it here names
  // the host and leaves the input a reader lands on unnamed. A MUD field that
  // carries neither is handed the label as `aria-label`; one that names itself
  // is left alone.
  const control = isValidElement(children) ? withFieldProps(children, label, error) : children;

  return (
    <label
      className={`simsm-form-page__field ${fullWidth ? 'simsm-form-page__field--full' : ''}`}
      data-invalid={error ? 'true' : undefined}
    >
      <span className="simsm-form-page__field-label">
        {label}
        {info ? (
          <MudTooltip content={info} position="top">
            <span
              slot="trigger"
              className="simsm-form-page__field-info"
              role="img"
              aria-label={info}
            >
              <MudIcon name="circle-info" variant="filled" size={16} />
            </span>
          </MudTooltip>
        ) : null}
        {required ? (
          <span className="simsm-form-page__field-req" aria-hidden="true">
            {' '}
            *
          </span>
        ) : null}
      </span>
      {control}
      {/* Icon-less on purpose: the wireframe draws field hints and errors as
          plain coloured text (`.field .hint`, `body-s text-danger`), and the
          small rung of mud-inline-message is exactly that text. */}
      {error ? (
        <MudInlineMessage variant="error" size="small" hideIcon role="alert">
          {error}
        </MudInlineMessage>
      ) : hint ? (
        <MudInlineMessage variant="info" size="small" hideIcon>
          {hint}
        </MudInlineMessage>
      ) : null}
    </label>
  );
}

/**
 * A row of fields inside a section.
 *
 * `columns` states the count instead of leaving it to `auto-fit`, which is what
 * the wireframe does (`.grid-2`, `.grid-3`). The difference shows on a row with
 * one field in it: `auto-fit` stretches a lone date picker across the whole
 * column, where the wireframe leaves it at half. Omitting `columns` keeps the
 * old auto-fit behaviour, which is what the other six editors are drawn with.
 */
export function FormRow({
  children,
  columns,
  as = 'div',
}: {
  children: ReactNode;
  columns?: 2 | 3;
  /** `dl` for a row of read-only pairs — see FormValue. */
  as?: 'div' | 'dl';
}): JSX.Element {
  const className =
    `simsm-form-page__row ${columns ? `simsm-form-page__row--${columns}` : ''}`.trim();
  if (as === 'dl') return <dl className={className}>{children}</dl>;
  return <div className={className}>{children}</div>;
}

/**
 * A label and a value, laid out exactly as FormField lays out a label and a control.
 *
 * A record and its editor are the same page in two states, and until this existed they
 * were two layouts: the record drew its own four-column grid of pairs while the editor
 * used the section bands, so a field sat in a different place depending on whether you
 * were reading it or changing it.
 *
 * `dt`/`dd` rather than FormField's `label`: nothing here is labelling a control, and a
 * `<label>` with no input is a label pointing at nothing. The wrapping `div` inside a
 * `dl` is what HTML allows for grouping one term with one definition, which is why
 * FormRow takes `as="dl"`.
 */
export function FormValue({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <div className="simsm-form-page__field">
      <dt className="simsm-form-page__field-label">{label}</dt>
      <dd className="simsm-form-page__field-value">{children}</dd>
    </div>
  );
}
